import { useRef } from "react";

// Highchart
import { PolarChart } from "store/slice/charts";

const useRenderTicksSpiderweb = () => {
    const yAxisTicksRef = useRef<Highcharts.SVGElement[]>([]);
    const yAxisTicksExportRef = useRef<Highcharts.SVGElement[]>([]);

    const renderYAxisTicks = (chart: PolarChart, isExporting = false, tickSteps = 20) => {
        const renderer = chart.renderer;

        if (isExporting) {
            yAxisTicksExportRef.current.forEach((el) => el.destroy());
            yAxisTicksExportRef.current = [];
        } else {
            yAxisTicksRef.current.forEach((el) => el.destroy());
            yAxisTicksRef.current = [];
        }

        // Use chart yAxis min and max to create the array of ticks (using tickSteps)
        const yAxis = chart.yAxis[0];
        const min = yAxis.min ?? 0;
        const max = yAxis.max ?? 100;
        const ticks: number[] = [];

        for (let tick = min; tick <= max; tick += tickSteps) {
            ticks.push(tick);
        }

        // Get the center of the plot
        const pane = chart.pane[0];
        const [cx, cy, diameter] = pane.center;

        // Get maximum radius based on pane diameter
        const maxRadius = diameter / 2.5;

        // Number should appear on a side, this is around degree 225
        const angleDeg = 225;
        const angleRad = (angleDeg * Math.PI) / 180;

        const createdLabels: Highcharts.SVGElement[] = [];

        ticks.forEach((tick) => {
            const ratio = tick / 100;
            const r = maxRadius * ratio;

            let x = cx + Math.cos(angleRad) * r;
            let y = cy + Math.sin(angleRad) * r;

            if (tick === 0) {
                x -= 6;
            }

            if (tick === 20) {
                x -= 14;
            }

            if (tick === 40) {
                x -= 8;
                y += 2;
            }

            const label = renderer
                .text(String(tick), x, y)
                .css({
                    color: "#84acbc",
                    fontSize: "11px",
                })
                .attr({
                    zIndex: 5,
                })
                .add();

            createdLabels.push(label);
        });

        if (isExporting) {
            yAxisTicksExportRef.current = createdLabels;
        } else {
            yAxisTicksRef.current = createdLabels;
        }
    };

    const outerYAxisGridRef = useRef<Highcharts.SVGElement | null>(null);

    const renderOuterYAxisGrid = (chart: PolarChart) => {
        const pane = chart.pane[0];
        const center = pane?.center;

        if (!center) return;

        const [centerX, centerY, diameter] = center;

        const absoluteCenterX = chart.plotLeft + centerX;
        const absoluteCenterY = chart.plotTop + centerY;
        const radius = diameter / 2;

        outerYAxisGridRef.current?.destroy();

        const path: Highcharts.SVGPathArray = [
            ["M", absoluteCenterX, absoluteCenterY - radius],
            ["L", absoluteCenterX + radius, absoluteCenterY],
            ["L", absoluteCenterX, absoluteCenterY + radius],
            ["L", absoluteCenterX - radius, absoluteCenterY],
            ["L", absoluteCenterX, absoluteCenterY - radius],
        ];

        outerYAxisGridRef.current = chart.renderer
            .path(path)
            .attr({
                stroke: "#84acbc",
                "stroke-width": 1,
                fill: "none",
                zIndex: 1,
            })
            .add();
    };

    return {
        renderYAxisTicks,
        renderOuterYAxisGrid,
    };
};

export default useRenderTicksSpiderweb;
